import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@reflex/ui/components/ui/card';
import { PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { MetricBlock } from '@/components/shared/MetricBlock';
import { Button } from '@reflex/ui/components/ui/button';
import { capitalize, timeAgo } from '@reflex/ui/lib/format';
import { WebhookStatusBadge } from './WebhookStatusBadge';
import { RecentEventsTable } from './RecentEventsTable';
import type { WebhookSourceSummary } from '@/lib/types';

const SOURCE_LABELS: Record<string, string> = {
  github: 'GitHub',
  gitlab: 'GitLab',
  bitbucket: 'Bitbucket',
};

export function WebhookSourceCard({ source }: { source: WebhookSourceSummary }) {
  const [expanded, setExpanded] = useState(false);
  const label = SOURCE_LABELS[source.source] ?? capitalize(source.source);
  const hasActivity = source.lastReceivedAt !== null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            <div>
              <PanelTitle as="h2" size="sm">
                {label}
              </PanelTitle>
              <CardDescription className="text-xs mt-0.5">
                {hasActivity
                  ? `Last received ${timeAgo(source.lastReceivedAt!)}`
                  : 'No retained webhook activity'}
              </CardDescription>
            </div>
          </div>
          <WebhookStatusBadge source={source} />
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="grid grid-cols-3 gap-4">
          <MetricBlock value={source.totalReceived} caption="Received" />
          <MetricBlock
            value={source.totalErrors}
            caption="Errors"
            valueClassName={source.totalErrors > 0 ? 'text-status-error' : undefined}
          />
          <MetricBlock
            value={
              source.totalReceived > 0
                ? `${(((source.totalReceived - source.totalErrors) / source.totalReceived) * 100).toFixed(0)}%`
                : '—'
            }
            caption="Success rate"
          />
        </div>

        {source.lastError && (
          <div className="mt-3 rounded-md border border-status-error/30 bg-status-error/10 px-3 py-2">
            <div className="flex items-center gap-1.5 text-xs text-status-error mb-0.5">
              <AlertTriangle className="h-3 w-3" />
              <span className="font-medium">Last error</span>
              <span className="text-status-error ml-auto">{timeAgo(source.lastError.at)}</span>
            </div>
            <p className="text-xs text-status-error break-all">{source.lastError.message}</p>
          </div>
        )}

        {expanded && (
          <div className="mt-4">
            <SectionLabel as="h3" className="mb-2">
              Recent events ({source.recentEvents.length})
            </SectionLabel>
            <RecentEventsTable events={source.recentEvents} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
